import { CENTER_OF_EXCELLENCE } from '../../centerOfExcellence/types'
import { TOOLKIT_TYPE, WELLNESS_TOOLKIT_PROMOTION } from '../../wellnessToolkit/types'
import { SEARCHABLE_ITEMS } from '../searchableItems'
import { SEARCHABLE_ITEM_ID, SearchableItemMetadata } from '../types'
import { EligibilityDeps } from './types'

const isHealthPlanPaymentVisible = (deps: EligibilityDeps): boolean =>
  !deps.isHideCostAndSessionLimitEnabled &&
  !deps.isInternational &&
  (deps.healthPlanExtensionEnabled ||
    deps.hasCostSharingCard ||
    (deps.hasBcxProvider && !(deps.infractionMessagingDisabled && deps.cancellationLimitCoversSessionLimit)))

const isInfractionCardSectionVisible = (deps: EligibilityDeps): boolean =>
  deps.hasBcxProvider &&
  !deps.isInternational &&
  (!deps.infractionMessagingDisabled ||
    (deps.infractionMessagingDisabled && deps.healthPlanExtensionEnabled && deps.hasUpcomingHPIAppointments))

/**
 * Map of item ID to eligibility function.
 * If an item is not in this map, it is considered always eligible (requires auth only).
 */
const ITEM_ELIGIBILITY: Partial<Record<SEARCHABLE_ITEM_ID, (deps: EligibilityDeps) => boolean>> = {
  // ============ TRIAGE ============
  [SEARCHABLE_ITEM_ID.ADULT_TRIAGE]: (deps) => deps.isSelfSearchAvailable,
  [SEARCHABLE_ITEM_ID.CHILD_TRIAGE]: (deps) => !deps.shouldHideChildSearchCTA,

  // ============ PROGRAMS (DPLs) ============
  [SEARCHABLE_ITEM_ID.COACHING]: (deps) => deps.isCoachingEnabled,
  [SEARCHABLE_ITEM_ID.PARENTING_COACHING]: (deps) => deps.isCoachingForParentsEnabled,
  [SEARCHABLE_ITEM_ID.COUPLES_THERAPY]: (deps) => deps.isDirectAccessTherapyEnabled,
  [SEARCHABLE_ITEM_ID.FAMILY_THERAPY]: (deps) => deps.isDirectAccessTherapyEnabled,
  [SEARCHABLE_ITEM_ID.MEDICATION_CONSULT]: (deps) => deps.isMedsEnabled,
  [SEARCHABLE_ITEM_ID.RENEW_AUD]: (deps) => deps.isAUDSupported && !deps.isUserTeen,

  // ============ COMPLEX CARE / EXTERNAL ============
  [SEARCHABLE_ITEM_ID.COMPLEX_CARE]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_ITEM_ID.DBT]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_ITEM_ID.IOP]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_ITEM_ID.PHP]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_ITEM_ID.RESIDENTIAL]: (deps) => deps.isComplexCareEnabled,
  [SEARCHABLE_ITEM_ID.INPATIENT]: (deps) => deps.isComplexCareEnabled,

  // ============ LIBRARY ============
  [SEARCHABLE_ITEM_ID.LIBRARY]: (deps) => deps.isEssentialsEnabled,
  [SEARCHABLE_ITEM_ID.MEDITATION]: (deps) => deps.isEssentialsEnabled,
  [SEARCHABLE_ITEM_ID.EVENTS]: (deps) => deps.isLibraryEventsEnabled,

  // ============ CARE ============
  [SEARCHABLE_ITEM_ID.CARE_TAB]: (deps) => deps.shouldShowCareTabInitial || deps.activitiesEnabled,
  [SEARCHABLE_ITEM_ID.SESSIONS]: (deps) => deps.showSessions,
  [SEARCHABLE_ITEM_ID.WELLNESS_CHECK_IN]: (deps) =>
    deps.isWellnessCheckInEnabled && deps.isEssentialsEnabled && deps.isWellnessCheckInLanguageSupported,

  // ============ ACCOUNT ============
  [SEARCHABLE_ITEM_ID.SESSION_HISTORY]: (deps) =>
    (deps.hasSessionHistory || !deps.isInternational || deps.hasAccessToInternationallySupportedTreatmentOptions) &&
    !deps.forceAdultFlowsToHPI,
  [SEARCHABLE_ITEM_ID.COVERAGE_INFO]: (deps) =>
    !deps.isHealthPlanDirect &&
    !deps.adultTriageDisabled &&
    !deps.forceAdultFlowsToHPI &&
    !deps.isHideCostAndSessionLimitEnabled,
  [SEARCHABLE_ITEM_ID.HEALTH_PLAN_PAYMENT]: isHealthPlanPaymentVisible,
  [SEARCHABLE_ITEM_ID.ADD_DEPENDENT]: (deps) =>
    !deps.shouldHideChildSearchCTA &&
    !deps.isUserMinor &&
    !deps.isInternational &&
    !deps.hideDependentField &&
    deps.isFamilyHubEnabled,
  [SEARCHABLE_ITEM_ID.DISPLAY_LANGUAGE]: (deps) => deps.isDisplayLanguageEnabled,
  [SEARCHABLE_ITEM_ID.ADD_HEALTH_PLAN]: (deps) => isHealthPlanPaymentVisible(deps) && deps.isClientHPI,
  [SEARCHABLE_ITEM_ID.ADD_PAYMENT_METHOD]: (deps) => isHealthPlanPaymentVisible(deps) && deps.isClientHPI,
  [SEARCHABLE_ITEM_ID.LATE_CANCELLATION_PAYMENT]: (deps) =>
    isHealthPlanPaymentVisible(deps) && isInfractionCardSectionVisible(deps),
  [SEARCHABLE_ITEM_ID.NOTIFICATIONS]: (deps) => deps.hasEpisode,
  [SEARCHABLE_ITEM_ID.PRIVACY_LEGAL]: (deps) => deps.hasBcxProvider,
  [SEARCHABLE_ITEM_ID.CLINICAL_DATA_SHARING]: (deps) => deps.isClientDataShareEnabled && !deps.isUserMinor,
  [SEARCHABLE_ITEM_ID.FORKED_TRIAGE]: (deps) => deps.showForkedTriage,
  [SEARCHABLE_ITEM_ID.MFA]: (deps) => !deps.isMFARequiredForUsers,
  [SEARCHABLE_ITEM_ID.TRANSFER_ACCOUNT]: (deps) => deps.isMemberSwitchEmployerSelfServeEnabled,

  // ============ EXTERNAL LINKS ============
  [SEARCHABLE_ITEM_ID.LYRA_APP]: (deps) => deps.showLyraApp,
}

/**
 * Mapping from item ID to toolkit enum.
 * Used for convention-based toolkit eligibility.
 */
const TOOLKIT_ID_TO_ENUM: Partial<Record<SEARCHABLE_ITEM_ID, TOOLKIT_TYPE>> = {
  [SEARCHABLE_ITEM_ID.TOOLKIT_RELATIONSHIPS]: WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS,
  [SEARCHABLE_ITEM_ID.TOOLKIT_WORK]: WELLNESS_TOOLKIT_PROMOTION.WORK,
  [SEARCHABLE_ITEM_ID.TOOLKIT_FAMILY]: WELLNESS_TOOLKIT_PROMOTION.FAMILY,
  [SEARCHABLE_ITEM_ID.TOOLKIT_NEURODIVERSITY]: CENTER_OF_EXCELLENCE.NEURODIVERSITY,
  [SEARCHABLE_ITEM_ID.TOOLKIT_HEALTH_CHALLENGES]: CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES,
  [SEARCHABLE_ITEM_ID.TOOLKIT_PARENTING]: WELLNESS_TOOLKIT_PROMOTION.PARENT,
  [SEARCHABLE_ITEM_ID.TOOLKIT_VALUES]: WELLNESS_TOOLKIT_PROMOTION.PURPOSE,
  [SEARCHABLE_ITEM_ID.TOOLKIT_TRAUMA]: WELLNESS_TOOLKIT_PROMOTION.TRAUMA,
}

/**
 * Determines if an item is eligible based on the provided dependencies.
 *
 * @param itemId - The ID of the item to check
 * @param deps - The eligibility dependencies (built from existing selectors/hooks)
 * @returns true if the item is eligible, false otherwise
 */
export const getItemEligibility = (itemId: SEARCHABLE_ITEM_ID, deps: EligibilityDeps): boolean => {
  // Check for toolkit items using convention-based approach
  const toolkitEnum = TOOLKIT_ID_TO_ENUM[itemId]
  if (toolkitEnum) {
    return deps.toolkitAvailability[toolkitEnum] ?? false
  }

  // Check for explicit eligibility function
  const eligibilityFn = ITEM_ELIGIBILITY[itemId]
  if (eligibilityFn) {
    return eligibilityFn(deps)
  }

  // If no eligibility function defined, item is available by default (requires auth only)
  return true
}

/**
 * Filters searchable items based on eligibility.
 *
 * @param deps - The eligibility dependencies
 * @param items - Optional array of items to filter. Defaults to SEARCHABLE_ITEMS.
 * @returns Array of eligible searchable items
 */
export const getEligibleSearchableItems = (
  deps: EligibilityDeps,
  items: SearchableItemMetadata[] = SEARCHABLE_ITEMS,
): SearchableItemMetadata[] => {
  return items.filter((item) => getItemEligibility(item.id, deps))
}
